import type { ProjectRightRailItemResponse } from '@protoss/shared';
import * as React from 'react';
import { apiFetch } from '../../../lib/apiClient';

interface UseProjectRightRailResult {
  items: ProjectRightRailItemResponse[];
  isLoading: boolean;
  error: string | null;
}

/**
 * useProjectRightRail - fetch the dynamic right-rail panel catalog for a project.
 *
 * Calls GET /api/project/:uuid/right-rail, which returns right-rail panel items
 * with entitlement and role state resolved server-side.
 *
 * The hook re-fetches automatically when projectUuid changes.
 *
 * Context filtering (applicableMenuGroups) is NOT applied here - the caller
 * (ProjectPage) applies it based on the active left-nav item.
 *
 * Authorization: Firebase ID token is attached to the request.
 */
export function useProjectRightRail(projectUuid: string | undefined): UseProjectRightRailResult {
  const [items, setItems] = React.useState<ProjectRightRailItemResponse[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const fetchRail = React.useCallback(async () => {
    if (!projectUuid) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiFetch<{ items: ProjectRightRailItemResponse[] }>(
        `/api/project/${projectUuid}/right-rail`
      );
      setItems(data.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch right-rail items');
    } finally {
      setIsLoading(false);
    }
  }, [projectUuid]);

  React.useEffect(() => {
    fetchRail();
  }, [fetchRail]);

  return { items, isLoading, error };
}
